import React, { useEffect, useMemo, useRef, useState } from 'react';
import './Panel.scss';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAccount } from '../../../contexts/account/AccountContexts';
import Loader from '../loader/Loader';
import { useTranslation } from 'react-i18next';
import {
  AGENCY_STATS,
  ALL,
  CONTEXT_ANALYSIS,
  BOTS_ACTIVITY,
  BOTS_ACTIVITY_ANALYSIS,
  BOT_ACCESS,
  GEO_CHECKLIST,
  GEO_REPORT,
  INSIGHTS,
  INTEGRATIONS,
  KEYWORDS,
  PRICING,
  PROJECTS,
  PROMPTS,
  SOURCES,
  SUGGESTED_PROMPTS,
  TAGS,
} from '../../../routes/path';
import { useGetCloudflareStatus } from '../../../api/hooks/bot-sources/useGetCloudflareStatus';
// Fastly integration is temporarily hidden from clients.
// import { useGetFastlyStatus } from '../../../api/hooks/bot-sources/useGetFastlyStatus';
import { useGetLogUploads } from '../../../api/hooks/bot-sources/useGetLogUploads';
import { ProjectResponse } from '../../../models/projects/ProjectResponse';
import LoginBox from '../../login-box/LoginBox';
import emptyProjectIcon from '../../../../assets/icons/empty-project.svg';
import { Button } from 'primereact/button';
import useDisabledAccount from '../../../hooks/account/UseDisabledAccount';
import useBilling from '../../../hooks/account/UseBilling';
import CustomTooltip from '../tooltip/CustomTooltip';
import { getProjectsFromAccount } from '../../../helpers/project/ProjectHelper';
import { PRIVACY_POLICY } from '../../../utils/urls';
import { getSubmenuHeight } from './helpers/PanelHelper';
import useStickyObserver from './hooks/UseStickyObserver';
import SimpleBar from 'simplebar-react';
import AdvisorBox from '../advisor-box/AdvisorBox';
import { useProjectGroups } from '../../../hooks/projects/UseProjectGroups';

const Panel: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { isDisabled } = useDisabledAccount();
  const canShowBilling = useBilling();

  const { accountResponse, projectResponse, setShowVideoTutorial } = useAccount();
  const [isOpen, setIsOpen] = useState(false);
  const [favicons, setFavicons] = useState<Record<string, string>>({});
  const [projects, setProjects] = useState<ProjectResponse[]>([]);
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  const [collapsedDropdownGroups, setCollapsedDropdownGroups] = useState<Set<number>>(new Set());
  const submenuRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);

  const { data: projectGroups = [] } = useProjectGroups();

  useEffect(() => {
    document.body.classList.remove('visible', 'white');
  }, []);
  useStickyObserver();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        submenuRef.current &&
        !submenuRef.current.contains(event.target as Node)
      ) {
        setIsSubmenuOpen(false);
      }
    };

    if (isSubmenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSubmenuOpen]);

  // Close dropdown on panel scroll
  useEffect(() => {
    if (!isSubmenuOpen || !submenuRef.current) return;
    const panelMenu = submenuRef.current.closest('.panel__menu');
    if (!panelMenu) return;
    const handleScroll = () => setIsSubmenuOpen(false);
    panelMenu.addEventListener('scroll', handleScroll);
    return () => panelMenu.removeEventListener('scroll', handleScroll);
  }, [isSubmenuOpen]);

  useEffect(() => {
    const newFavicons: Record<string, string> = {};

    projects.forEach((project) => {
      if (!favicons[project.domain]) {
        newFavicons[project.domain] = project.domain
          ? `https://favicon.is/${project.domain}`
          : emptyProjectIcon;
      }
    });

    setFavicons((prev) => ({ ...prev, ...newFavicons }));
  }, [projects]);

  useEffect(() => {
    if (!accountResponse || !projectResponse) {
      return;
    }

    const filteredProjects = getProjectsFromAccount(accountResponse, projectResponse);
    setProjects(filteredProjects);
  }, [accountResponse, projectResponse]);

  // For the sidebar "Bots activity" link we want to skip the connect-page bounce
  // when the project already has a data source. These two cheap reads (cached
  // 30 s / 1 min) are shared with the BotsActivity pages, so they don't
  // double-fetch when the user is actually in that area. Disabled when there is
  // no current project.
  const currentProjectId = useMemo(() => {
    // Match `<route>/:projectId` for any project-scoped route. Route paths come
    // from the shared `path` module so the list stays in sync with the router.
    const escapeForRegex = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const routes = [
      PROJECTS,
      SUGGESTED_PROMPTS,
      TAGS,
      KEYWORDS,
      PROMPTS,
      SOURCES,
      INSIGHTS,
      CONTEXT_ANALYSIS,
      GEO_CHECKLIST,
      BOTS_ACTIVITY,
      BOT_ACCESS,
    ];
    const patterns = routes.map((r) => new RegExp(`^${escapeForRegex(r)}/(\\d+)`));

    for (const pattern of patterns) {
      const match = location.pathname.match(pattern);
      if (match) {
        return Number(match[1]);
      }
    }

    return null;
  }, [location.pathname]);

  const botsActivityProbeId = currentProjectId ?? null;
  const botsActivityCloudflareStatus = useGetCloudflareStatus(botsActivityProbeId);
  // Fastly integration is temporarily hidden from clients.
  // const botsActivityFastlyStatus = useGetFastlyStatus(botsActivityProbeId);
  const botsActivityLogUploads = useGetLogUploads(botsActivityProbeId);
  // Probe every active source — if any one is connected, the "Bots activity"
  // sidebar link goes straight to the analysis page instead of bouncing through
  // the connect page. Skipping a provider here causes a visible redirect flash
  // for users connected to that source.
  const botsActivityHasAnyDataSource =
    !!botsActivityCloudflareStatus.data?.isConnected ||
    // !!botsActivityFastlyStatus.data?.isConnected ||
    botsActivityLogUploads.data.length > 0;

  const currentProject = useMemo(() => {
    if (!currentProjectId) {
      return null;
    }

    const projectsToSearch =
      accountResponse && accountResponse.isLimitedSuperAdmin ? projectResponse || projects : projects;
    const found = projectsToSearch.find((p) => p.id === currentProjectId);
    if (found) return found;

    // The URL has a project id but the loaded project list does not contain it
    // (superadmin opening a project outside their account, or a brief render
    // before `projects` is populated). Returning `null` would hide the entire
    // project section in the sidebar, including the Bots Activity / Bot Access nav links,
    // even though we know which project the user is on. Fall back to a stub so
    // the submenu renders with sane defaults — the labels are project-agnostic
    // and the links use the id directly.
    return {
      id: currentProjectId,
      projectLabel: `Project ${currentProjectId}`,
      domain: '',
      defaultPeriod: 'DAILY',
      demoVersion: null,
      status: 'ACTIVE',
    } as ProjectResponse;
  }, [currentProjectId, projects, projectResponse, accountResponse]);

  const hasProjectGroupsAddon = !!accountResponse?.addons?.includes('PROJECT_GROUPS');
  const sortedProjectGroups = useMemo(
    () => [...projectGroups].sort((a, b) => a.position - b.position),
    [projectGroups]
  );

  const toggleSubmenu = (e: React.MouseEvent) => {
    if (!isSubmenuOpen) {
      // When opening, collapse all groups except the current project's group.
      // Ungrouped projects are rendered flat (no collapsible section) so no sentinel
      // is added here.
      const currentGroupId = currentProject?.projectGroupId;
      const allGroupIds = new Set(sortedProjectGroups.map((g) => g.id));
      if (currentGroupId !== null && currentGroupId !== undefined) {
        allGroupIds.delete(currentGroupId);
      }
      setCollapsedDropdownGroups(allGroupIds);

      // Match original position: absolute; top: 42px; left: 33px; width: 100% relative to submenuRef
      if (submenuRef.current) {
        const rect = submenuRef.current.getBoundingClientRect();
        setDropdownPos({ top: rect.top + 42, left: rect.left + 33, width: rect.width });
      }
    }
    setIsSubmenuOpen(!isSubmenuOpen);
  };

  const renderCurrentProject = () => {
    if (!currentProject || !accountResponse) {
      return null;
    }

    const handleSubmenuItemClick = () => {
      setIsSubmenuOpen(false);
    };

    return (
      <div key={`project-submenu-${currentProject.id}`}>
        <div id={`project-btn-${currentProject.id}`} className="menu__item--active menu__item">
          <div className="menu__item-text-container">
            <span
              style={{
                backgroundImage: `url(${favicons[currentProject.domain]})`,
              }}
            />
            <span className="menu__text">
              {currentProject.projectLabel} {currentProject.demoVersion && '(Demo)'}
            </span>
          </div>
          <div className="menu__item-navigation">
            <button
              className={`menu__item-nav-button ${isSubmenuOpen ? 'menu__item-nav-button--active' : ''}`}
              onClick={toggleSubmenu}
            >
              <span className={`${isSubmenuOpen ? 'ico--rotate' : ''} ico--bottom ico--sm`} />
            </button>
          </div>
        </div>

        <div
          className={`menu__list menu__list--open`}
          style={{
            height:
              getSubmenuHeight(
                accountResponse,
                true,
                !!currentProject.demoVersion,
                currentProject.status === 'ACTIVE'
              ) + 'px',
          }}
        >
          <NavLink
            id={`overview-btn-${currentProject.id}`}
            to={`${PROJECTS}/${currentProject.id}/${currentProject.defaultPeriod.toLowerCase()}${ALL}`}
            className={({ isActive }) =>
              (isActive ? 'menu__item-small--active' : '') + ' menu__item-small'
            }
            onClick={handleSubmenuItemClick}
          >
            <i className="ico--overview ico--xs" />
            <span className="menu__text">{t('overview')}</span>
          </NavLink>
          {accountResponse.addons.includes('SUGGESTED_PROMPTS') && (
            <NavLink
              id={`suggested-btn-${currentProject.id}`}
              to={`${SUGGESTED_PROMPTS}/${currentProject.id}`}
              className={({ isActive }) =>
                (isActive ? 'menu__item-small--active' : '') + ' menu__item-small'
              }
              onClick={handleSubmenuItemClick}
            >
              <i className="ico--star ico--xs" />
              <span className="menu__text">{t('suggestedPrompts')}</span>
            </NavLink>
          )}
          {!currentProject.demoVersion && accountResponse.addons.includes('KEYWORDS_PAGE') && (
            <NavLink
              id={`keywords-btn-${currentProject.id}`}
              to={`${KEYWORDS}/${currentProject.id}`}
              className={({ isActive }) =>
                (isActive ? 'menu__item-small--active' : '') + ' menu__item-small'
              }
              onClick={handleSubmenuItemClick}
            >
              <i className="ico--globe ico--xs" />
              <span className="menu__text">{t('keywords')}</span>
            </NavLink>
          )}
          {accountResponse.addons.includes('PROMPT_GROUP') && !currentProject.demoVersion && (
            <NavLink
              id={`prompts-btn-${currentProject.id}`}
              to={`${PROMPTS}/${currentProject.id}`}
              className={({ isActive }) =>
                (isActive ? 'menu__item-small--active' : '') + ' menu__item-small'
              }
              onClick={handleSubmenuItemClick}
            >
              <i className="ico--question ico--xs" />
              <span className="menu__text">{t('prompts')}</span>
            </NavLink>
          )}
          {accountResponse.addons.includes('SOURCES_STATS') && (
            <NavLink
              id={`sources-btn-${currentProject.id}`}
              to={`${SOURCES}/${currentProject.id}/${currentProject.defaultPeriod.toLowerCase()}${ALL}`}
              className={({ isActive }) =>
                (isActive ? 'menu__item-small--active' : '') + ' menu__item-small'
              }
              onClick={handleSubmenuItemClick}
            >
              <i className="ico--sources ico--xs" />
              <span className="menu__text">{t('sources')}</span>
            </NavLink>
          )}
          {/*{accountResponse.addons.includes('INSIGHTS') && (*/}
          {/*  <NavLink*/}
          {/*    id={`sources-btn-${currentProject.id}`}*/}
          {/*    to={`${INSIGHTS}/${currentProject.id}/${currentProject.defaultPeriod.toLowerCase()}${ALL}`}*/}
          {/*    className={({ isActive }) =>*/}
          {/*      (isActive ? 'menu__item-small--active' : '') + ' menu__item-small'*/}
          {/*    }*/}
          {/*    onClick={handleSubmenuItemClick}*/}
          {/*  >*/}
          {/*    <i className="ico--zap ico--xs" />*/}
          {/*    <div className="indicator-container">*/}
          {/*      <span className="menu__text">{t('insights')}</span>*/}
          {/*    </div>*/}
          {/*  </NavLink>*/}
          {/*)}*/}
          {accountResponse.addons.includes('CONTEXT_INSIGHTS') &&
            currentProject.demoVersion === null &&
            currentProject.status === 'ACTIVE' && (
              <NavLink
                id={`context-analysis-btn-${currentProject.id}`}
                to={`${CONTEXT_ANALYSIS}/${
                  currentProject.id
                }/${currentProject.defaultPeriod.toLowerCase()}${ALL}`}
                className={({ isActive }) =>
                  (isActive ? 'menu__item-small--active' : '') + ' menu__item-small'
                }
                onClick={handleSubmenuItemClick}
              >
                <i className="ico--zap ico--xs" />
                <div className="indicator-container">
                  <span className="menu__text">{t('contextAnalysis.header')}</span>
                  <div className="indicator-container__right">
                    <span className="indicator indicator--beta">Beta</span>
                  </div>
                </div>
              </NavLink>
            )}
          {accountResponse.addons.includes('TAGS') && !currentProject.demoVersion && (
            <NavLink
              id={`tags-btn-${currentProject.id}`}
              to={`${TAGS}/${currentProject.id}`}
              className={({ isActive }) =>
                (isActive ? 'menu__item-small--active' : '') + ' menu__item-small'
              }
              onClick={handleSubmenuItemClick}
            >
              <i className="ico--bookmark ico--xs" />
              <span className="menu__text">{t('tags')}</span>
            </NavLink>
          )}
          <NavLink
            id={`geo-checklist-btn-${currentProject.id}`}
            to={`${GEO_CHECKLIST}/${currentProject.id}/topics-prompts`}
            className={({ isActive }) =>
              (isActive ? 'menu__item-small--active' : '') + ' menu__item-small'
            }
            onClick={handleSubmenuItemClick}
          >
            <i className="ico--clipboard ico--xs" />
            <span className="menu__text">{t('checklists')}</span>
          </NavLink>
          <NavLink
            id={`bots-activity-btn-${currentProject.id}`}
            to={
              botsActivityHasAnyDataSource
                ? BOTS_ACTIVITY_ANALYSIS(currentProject.id)
                : `${BOTS_ACTIVITY}/${currentProject.id}`
            }
            className={({ isActive }) =>
              (isActive ? 'menu__item-small--active' : '') + ' menu__item-small'
            }
            onClick={handleSubmenuItemClick}
          >
            <i className="ico--bot ico--xs" />
            <div className="indicator-container">
              <span className="menu__text">{t('botsActivity.navLabel')}</span>
              <div className="indicator-container__right">
                <span className="indicator indicator--beta">Beta</span>
              </div>
            </div>
          </NavLink>
          <NavLink
            id={`bot-access-btn-${currentProject.id}`}
            to={`${BOT_ACCESS}/${currentProject.id}`}
            className={({ isActive }) =>
              (isActive ? 'menu__item-small--active' : '') + ' menu__item-small'
            }
            onClick={handleSubmenuItemClick}
          >
            <i className="ico--crawl ico--xs" />
            <div className="indicator-container">
              <span className="menu__text">{t('botAccess.navLabel')}</span>
              <div className="indicator-container__right">
                <span className="indicator indicator--beta">Beta</span>
              </div>
            </div>
          </NavLink>
        </div>
        {isSubmenuOpen && dropdownPos && (
          <div
            className="menu__projects-dropdown"
            ref={dropdownRef}
            style={{
              position: 'fixed',
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
              zIndex: 1000,
            }}
          >
            <SimpleBar
              className="menu__projects-dropdown-list"
              style={{ maxHeight: 252 }}
              autoHide={false}
            >
              {hasProjectGroupsAddon && sortedProjectGroups.length > 0 ? (
                <>
                  {sortedProjectGroups.map((group) => {
                    const groupProjects = projects.filter((p) => p.projectGroupId === group.id);
                    if (groupProjects.length === 0) return null;
                    const isCollapsed = collapsedDropdownGroups.has(group.id);

                    return (
                      <div key={`dropdown-group-${group.id}`} className="menu__dropdown-group">
                        <div
                          className={`menu__dropdown-group-header${!collapsedDropdownGroups.has(group.id) ? ' menu__dropdown-group-header--active' : ''}${currentProject?.projectGroupId === group.id ? ' menu__dropdown-group-header--current' : ''}`}
                          onClick={() => {
                            setCollapsedDropdownGroups((prev) => {
                              const next = new Set(prev);
                              if (next.has(group.id)) next.delete(group.id);
                              else next.add(group.id);
                              return next;
                            });
                          }}
                        >
                          <span
                            className="menu__dropdown-group-dot"
                            style={{ backgroundColor: group.color || 'var(--grey-ux-400)' }}
                          />
                          <span className="menu__dropdown-group-name">{group.name}</span>
                          <span
                            className={`ico--up ico--xs menu__dropdown-group-chevron${!isCollapsed ? ' menu__dropdown-group-chevron--expanded' : ''}`}
                          />
                        </div>
                        <div className={`menu__dropdown-group-content${!isCollapsed ? ' menu__dropdown-group-content--open' : ''}`}>
                          <div className="menu__dropdown-group-content-inner">
                            {groupProjects.map((project) => (
                              <NavLink
                                key={`project-submenu-btn-${project.id}`}
                                to={`${PROJECTS}/${project.id}/${project.defaultPeriod.toLowerCase()}${ALL}`}
                                className={`menu__item${project.id === currentProject?.id ? ' menu__item--active' : ''}`}
                                onClick={toggleSubmenu}
                              >
                                <div className="menu__item-text-container">
                                  <span
                                    style={{
                                      backgroundImage: `url(${favicons[project.domain]})`,
                                    }}
                                  />
                                  <span className="menu__text">
                                    {project.projectLabel} {project.demoVersion && '(Demo)'}
                                  </span>
                                </div>
                              </NavLink>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {projects
                    .filter((p) => !p.projectGroupId)
                    .map((project) => (
                      <NavLink
                        key={`project-submenu-btn-${project.id}`}
                        to={`${PROJECTS}/${project.id}/${project.defaultPeriod.toLowerCase()}${ALL}`}
                        className={`menu__item${project.id === currentProject?.id ? ' menu__item--active' : ''}`}
                        onClick={toggleSubmenu}
                      >
                        <div className="menu__item-text-container">
                          <span
                            style={{
                              backgroundImage: `url(${favicons[project.domain]})`,
                            }}
                          />
                          <span className="menu__text">
                            {project.projectLabel} {project.demoVersion && '(Demo)'}
                          </span>
                        </div>
                      </NavLink>
                    ))}
                </>
              ) : (
                projects.map((project) => (
                  <NavLink
                    key={`project-submenu-btn-${project.id}`}
                    to={`${PROJECTS}/${project.id}/${project.defaultPeriod.toLowerCase()}${ALL}`}
                    className={`menu__item${project.id === currentProject?.id ? ' menu__item--active' : ''}`}
                    onClick={toggleSubmenu}
                  >
                    <div className="menu__item-text-container">
                      <span
                        style={{
                          backgroundImage: `url(${favicons[project.domain]})`,
                        }}
                      />
                      <span className="menu__text">
                        {project.projectLabel} {project.demoVersion && '(Demo)'}
                      </span>
                    </div>
                  </NavLink>
                ))
              )}
            </SimpleBar>
          </div>
        )}
      </div>
    );
  };

  if (!accountResponse || !projects || !projectResponse) {
    return <Loader />;
  }

  return (
    <div className="panel">
      <div className="logo">
        <NavLink to={PROJECTS}>
          <div className="logo-box logo-box--app" />
        </NavLink>
      </div>
      <div className="panel__menu-panel">
        <div className="panel__menu menu">
          {!isDisabled && currentProject && (
            <>
              <div className="menu__separator" />
              <div className="panel__menu-title">{t('project')}</div>
              <div className="menu__submenu" ref={submenuRef}>
                {renderCurrentProject()}
              </div>
            </>
          )}

          <div className="menu__separator" />
          <div className="panel__menu-title">{t('tools')}</div>
          <div className="menu__tools">
            {!isDisabled && (
              <>
                <NavLink
                  id="projects-btn"
                  to={PROJECTS}
                  className={({ isActive }) => {
                    const isExactProjects = location.pathname === PROJECTS;
                    return (isExactProjects ? 'menu__item--active' : '') + ' menu__item';
                  }}
                >
                  <i className="ico--bookmark ico--sm" />
                  <span className="menu__text">{t('allProjects')}</span>
                </NavLink>
                <CustomTooltip
                  tooltipText={t('allProjects')}
                  targetRef="projects-btn"
                  isMobileTooltip={true}
                  direction="right"
                />
              </>
            )}
            {accountResponse.addons.includes('GEO_REPORT') && !isDisabled && (
              <>
                <NavLink
                  id="geo-audit-btn"
                  to={GEO_REPORT}
                  className={({ isActive }) => (isActive ? 'menu__item--active' : '') + ' menu__item'}
                >
                  <i className="ico--report ico--sm" />
                  <span className="menu__text">{t('geoReport.navLabel')}</span>
                </NavLink>
                <CustomTooltip
                  tooltipText={t('geoReport.navLabel')}
                  targetRef="geo-audit-btn"
                  isMobileTooltip={true}
                  direction="right"
                />
              </>
            )}
            {accountResponse.addons.includes('AGENCY') && (
              <>
                <NavLink
                  id="agency-stats-btn"
                  to={AGENCY_STATS}
                  className={({ isActive }) => (isActive ? 'menu__item--active' : '') + ' menu__item'}
                >
                  <i className="ico--chart ico--sm" />
                  <span className="menu__text">{t('agencyStats')}</span>
                </NavLink>
                <CustomTooltip
                  tooltipText={t('agencyStats')}
                  targetRef="agency-stats-btn"
                  isMobileTooltip={true}
                  direction="right"
                />
              </>
            )}
            {accountResponse.addons.includes('LOOKER_STUDIO') && (
              <>
                <NavLink
                  id="looker-studio-btn"
                  to={`${INTEGRATIONS}`}
                  className={({ isActive }) => (isActive ? 'menu__item--active' : '') + ' menu__item'}
                >
                  <i className="ico--globe ico--sm" />
                  <span className="menu__text">{t('integrations')}</span>
                </NavLink>
                <CustomTooltip
                  tooltipText={t('integrations')}
                  targetRef="looker-studio-btn"
                  isMobileTooltip={true}
                  direction="right"
                />
              </>
            )}
            {canShowBilling && (
              <>
                <NavLink
                  id="subscription-btn"
                  to={PRICING}
                  className={({ isActive }) => (isActive ? 'menu__item--active' : '') + ' menu__item'}
                >
                  <i className="ico--card ico--sm" />
                  <span className="menu__text">{t('subscription')}</span>
                </NavLink>
                <CustomTooltip
                  tooltipText={t('subscription')}
                  targetRef="subscription-btn"
                  isMobileTooltip={true}
                  direction="right"
                />
              </>
            )}
            <NavLink
              id="faq-btn"
              to="/faq"
              className={({ isActive }) => (isActive ? 'menu__item--active' : '') + ' menu__item'}
            >
              <i className="ico--chat ico--sm" />
              <span className="menu__text">{t('faq')}</span>
            </NavLink>
            <CustomTooltip
              tooltipText={t('faq')}
              targetRef="faq-btn"
              isMobileTooltip={true}
              direction="right"
            />
            <NavLink
              id="imporve-ai-btn"
              to="https://chatbeat.com/llm-seo"
              target="_blank"
              className="menu__item"
            >
              <i className="ico--chart ico--sm" />
              <span className="menu__text">{t('improveAIRanking')}</span>
            </NavLink>
            <CustomTooltip
              tooltipText={t('improveAIRanking')}
              targetRef="imporve-ai-btn"
              isMobileTooltip={true}
              direction="right"
            />
            <Button id="video-btn" onClick={() => setShowVideoTutorial(true)} className="menu__item">
              <i className="ico--video ico--sm" />
              <span className="menu__text">{t('videoTutorial')}</span>
            </Button>
            <CustomTooltip
              tooltipText={t('videoTutorial')}
              targetRef="video-btn"
              isMobileTooltip={true}
              direction="right"
            />
            <AdvisorBox />
          </div>
        </div>
        <div className="panel__end">
          <div className="panel__footer">
            <LoginBox />
          </div>
          <ul className="privacy-policy">
            <li>
              <Link to={PRIVACY_POLICY} target="_blank">
                {t('privacyPolicy')}
              </Link>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  (window as any).OneTrust?.ToggleInfoDisplay();
                }}
              >
                {t('customizeCookie')}
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Panel;
